import { useEffect, useState } from 'react';

import { PaginationState } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { ChronologyTimelineHeader } from './components/chronology-timeline-header';
import { ChronologyTimelineLoader } from './components/table-timeline/chronology-timeline-loader';
import { TableTimeline } from './components/table-timeline/table-timeline';
import { ViewMode } from './components/view-selector';
import { VisualTimeline } from './components/visual-timeline/visual-timeline';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useUpdateChronology } from '@/api/mutations/useUpdateChronology';
import { useGetChronology } from '@/api/queries/useGetChronology';
import { useGetChronologyFacts } from '@/api/queries/useGetChronologyFacts';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { FactDocViewer } from '@/new/components/doc-viewer/fact-doc-viewer';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';

export function Chronology() {
  const { chronologyId, matterId, subsetId } = useParams();
  if (!chronologyId || !matterId || !subsetId) return null;

  const [isDocViewerOpen, setIsDocViewerOpen] = useState(false);
  const [docId, setDocId] = useState<string | null>(null);
  const [factId, setFactId] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Table);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editedName, setEditedName] = useState('');

  // ----- Data fetching -----
  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);
  const { data: chronologyFacts, isLoading: isLoadingChronologyFacts } = useGetChronologyFacts({
    chronologyId,
    caseId: subsetId,
    pageIndex,
    pageSize,
    paginated: viewMode === ViewMode.Table,
  });
  const { data: chronology, refetch: refetchChronology } = useGetChronology({
    chronologyId,
    caseId: subsetId,
  });
  const { mutate: updateChronology } = useUpdateChronology({ chronologyId, caseId: subsetId });

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Chronologies', path: 'chronologies' },
  });

  useEffect(() => {
    trackPageView(PageName.Chronology);
  }, []);

  const openDocViewer = (docId: string, factId: string) => {
    setDocId(docId);
    setFactId(factId);
    setIsDocViewerOpen(true);
  };

  const handleEditClick = () => {
    setEditedName(chronology?.title ?? '');
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (editedName.trim()) {
      trackCustomEvent(TrackingEventsV2.UpdateChronologyName, { chronologyId, subsetId });
      updateChronology(editedName.trim(), {
        onSuccess: () => {
          refetchChronology();
          setIsEditDialogOpen(false);
        },
      });
    }
  };

  // Pagination
  const goToPage = (val: number) => {
    const newPaginationState = { pageIndex: val, pageSize: pageSize };
    setPagination(newPaginationState);
  };
  const prevPage = () => goToPage(pageIndex - 1);
  const nextPage = () => goToPage(pageIndex + 1);
  const totalPages = chronologyFacts?.pageCount;

  return (
    <div className="flex h-full flex-col">
      <Breadcrumb
        items={[
          ...breadcrumbItems.map((item, index) =>
            index === breadcrumbItems.length - 1 ? { ...item, active: false } : item,
          ),
          {
            href: `/matters/${matterId}/subsets/${subsetId}/chronologies/${chronologyId}`,
            label: chronology?.title ?? 'Chronology',
            active: true,
          },
        ]}
      />
      {viewMode === ViewMode.Table ? (
        <div className="flex flex-1 flex-col overflow-hidden">
          <PageContainer>
            <PageHeader>
              <div className="flex grow items-center gap-4">
                <PageTitle text={chronology?.title ?? ''} iconClass="lucide--git-fork" />
                <button onClick={handleEditClick}>
                  <Icon className="lucide--pencil text-base text-gray-400" />
                </button>
              </div>

              <ChronologyTimelineHeader
                chronologyTitle={chronology?.title ?? 'chronology'}
                canGetPrevPage={pageIndex > 0}
                canGetNextPage={pageIndex < (totalPages ?? 0) - 1}
                prevPage={prevPage}
                nextPage={nextPage}
                currentPage={pageIndex}
                noOfPages={totalPages}
                goToPage={goToPage}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
              />
            </PageHeader>
          </PageContainer>
          <div className="relative mb-2 flex w-full flex-1 gap-1 overflow-hidden px-4">
            {isLoadingChronologyFacts ? (
              <ChronologyTimelineLoader />
            ) : (
              <div
                className={`h-full overflow-y-scroll py-0.5 transition-all duration-300 ${isDocViewerOpen ? 'w-7/12' : 'w-full'}`}
              >
                <TableTimeline
                  onViewDocument={openDocViewer}
                  chronologyFacts={chronologyFacts?.chronologyFacts ?? []}
                />
              </div>
            )}
            <div
              className={`transition-all duration-300 ease-in-out ${
                isDocViewerOpen ? 'w-5/12 opacity-100' : 'w-0 opacity-0'
              } overflow-hidden rounded-md border border-gray-300 bg-white`}
            >
              <div
                className={`h-full w-full transition-transform duration-300 ease-in-out ${
                  isDocViewerOpen ? 'translate-x-0' : 'translate-x-full'
                }`}
              >
                {isDocViewerOpen && docId && subsetId && factId && (
                  <FactDocViewer
                    docId={docId}
                    factId={factId}
                    onClose={() => {
                      setDocId(null);
                      setFactId(null);
                      setIsDocViewerOpen(false);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <VisualTimeline
          chronology={chronologyFacts?.chronologyFacts ?? []}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
      )}

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Chronology Name</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              className="text-gray-800"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              placeholder="Enter chronology name"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSaveEdit();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSaveEdit} disabled={editedName === chronology?.title}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
